import React, { useEffect, useState } from "react";
import gql from "graphql-tag";
import { RouteComponentProps, navigate } from "@reach/router";
import { useMutation } from "react-apollo";
import queryString from "query-string";
import { useToggle, useStoreAppValue } from "@hooks";
import { Bugsnag, RailsUrl } from "@services";
import { ErrorLoading } from "@organisms";
import IntentLoadingView from "./IntentLoadingView";
import { IntentLoadingAnonymous } from "../IntentLoadingAnonymous";

// == Types ================================================================

interface IRouteProps {
  runMutation: boolean;
}

type TProps = RouteComponentProps<IRouteProps>;

// == Constants ============================================================
const INTENT_CREATE = gql`
  mutation IntentLoadingViewMutation($input: GiftcardIntentCreateInput!) {
    GiftcardIntentCreate(input: $input) {
      ... on MutationError {
        errors {
          input
          inputErrors
        }
      }
      ... on GiftcardIntent {
        id
        token
        ...IntentLoadingViewFragment
      }
    }
  }
  ${IntentLoadingView.fragments}
`;

IntentLoadingViewMutation.defaultProps = {
  runMutation: false,
};

// == Component ============================================================

export function IntentLoadingViewMutation({ location, runMutation }: TProps) {
  const [giftcardIntent, setGiftcardIntent] = useState();
  const { currentUser } = useStoreAppValue();
  const [showAnonForm, toggleAnonForm] = useToggle(false);
  const [hasError, toggleError] = useToggle(false);
  const [intentCreateMutation] = useMutation<$FixMe, $FixMe>(INTENT_CREATE);
  useEffect(() => {
    if (runMutation) {
      const params = queryString.parse(location.search);
      const {
        amountCents,
        deviceAttributes,
        fundraiserId,
        isGift,
        isPhysical,
        linkId,
        quantity,
      } = params;
      try {
        intentCreateMutation({
          variables: {
            input: {
              amountCents: parseInt(amountCents, 10),
              deviceAttributes: JSON.parse(deviceAttributes),
              fundraiserId,
              isGift: isGift === "true",
              isPhysical: isPhysical === "true",
              linkId,
              quantity: parseInt(quantity, 10),
            },
          },
        })
          .then(({ data }) => {
            const { GiftcardIntentCreate } = data;
            const { token: intentToken } = GiftcardIntentCreate;
            navigate(RailsUrl.intentUrl({ intentToken }), { replace: true });
            if (!GiftcardIntentCreate || GiftcardIntentCreate.errors) {
              toggleError();
            } else {
              setGiftcardIntent(GiftcardIntentCreate);
              if (!currentUser) {
                toggleAnonForm();
              } else {
                setTimeout(() => {
                  navigate(RailsUrl.checkoutUrl(GiftcardIntentCreate.token), { replace: true });
                }, 3000);
              }
            }
          })
          .catch((e) => {
            toggleError(true);
            Bugsnag.notify(e);
          });
      } catch (e) {
        toggleError(true);
        Bugsnag.notify(e);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (hasError) return <ErrorLoading />;

  if (showAnonForm)
    return (
      <IntentLoadingAnonymous
        giftcardIntent={giftcardIntent}
        location={location}
        toggleAnonForm={toggleAnonForm}
        toggleError={toggleError}
      />
    );

  return <IntentLoadingView giftcardIntent={giftcardIntent} />;
}
// == Styles ===============================================================
